import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Autocomplete,
  AutocompleteRenderOptionState,
  CircularProgress,
  MenuItem,
  TextField,
} from "@mui/material";
import React from "react";

export const DEFAULT_SEARCH_THRESHOLD = 5;

export type SelectOption = {
  label: string;
  value: string | number;
  raw?: any;
};

export interface SearchableSelectProps {
  options: SelectOption[];
  value: string | number | null | undefined;
  onChange: (value: string | number, raw?: any) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  loading?: boolean;
  searchable?: boolean;
  searchThreshold?: number;
  renderOption?: (option: SelectOption) => React.ReactNode;
  fullWidth?: boolean;
  className?: string;
  showAllOption?: { label: string; value: string | number };
  required?: boolean;
  margin?: "none" | "dense" | "normal";
  variant?: "outlined" | "filled" | "standard";
  name?: string;
  size?: "small" | "medium";
  inputSx?: React.CSSProperties;
}

const sameValue = (a: string | number | null | undefined, b: string | number | null | undefined) =>
  String(a ?? "") === String(b ?? "");

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  label,
  disabled,
  error,
  helperText,
  loading,
  searchable,
  searchThreshold = DEFAULT_SEARCH_THRESHOLD,
  renderOption,
  fullWidth = true,
  className,
  showAllOption,
  required,
  margin = "dense",
  variant = "outlined",
  name,
  size,
  inputSx,
}) => {
  const allOptions: SelectOption[] = showAllOption
    ? [{ label: showAllOption.label, value: showAllOption.value }, ...options]
    : options;

  const shouldSearch = searchable ?? allOptions.length > searchThreshold;

  if (!shouldSearch) {
    return (
      <TextField
        select
        name={name}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        helperText={helperText}
        fullWidth={fullWidth}
        className={className}
        required={required}
        margin={margin}
        variant={variant}
        size={size}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#FFF",
            ...(inputSx || {}),
          },
        }}
        SelectProps={{
          IconComponent: KeyboardArrowDownIcon,
          endAdornment: loading ? (
            <CircularProgress size={18} style={{ marginRight: 28 }} />
          ) : undefined,
        }}
      >
        {allOptions.map((o) => (
          <MenuItem key={String(o.value)} value={o.value}>
            {renderOption ? renderOption(o) : o.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  const selected =
    allOptions.find((o) => sameValue(o.value, value)) ?? null;

  return (
    <Autocomplete<SelectOption, false, boolean, false>
      options={allOptions}
      value={selected as SelectOption}
      disableClearable={!!showAllOption}
      onChange={(_, opt) => {
        if (!opt) {
          if (showAllOption) {
            onChange(showAllOption.value, undefined);
            return;
          }
          onChange("", undefined);
          return;
        }
        onChange(opt.value, opt.raw);
      }}
      getOptionLabel={(o) => o?.label ?? ""}
      isOptionEqualToValue={(a, b) => sameValue(a?.value, b?.value)}
      disabled={disabled}
      loading={loading}
      fullWidth={fullWidth}
      className={className}
      size={size}
      popupIcon={<KeyboardArrowDownIcon />}
      sx={{
        "& .MuiOutlinedInput-root": {
          backgroundColor: "#FFF",
          ...(inputSx || {}),
        },
      }}
      renderOption={
        renderOption
          ? (props, option: SelectOption, _state: AutocompleteRenderOptionState) => (
              <li {...props} key={String(option.value)}>
                {renderOption(option)}
              </li>
            )
          : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          required={required}
          variant={variant}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SearchableSelect;
